import { DEFAULT_GC_TIME, DEFAULT_STALE_TIME } from '@/client/constants';
import { useClientStore } from '@/client/stores/client';
import { withErrorHandling } from '@/client/utils/errors';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

/**
 * Reviews & ratings — `/api/admin/feedback` (ff-api admin.feedback).
 *   GET    ?entity_id=<eid> | ?search= (+page,page_size) → { feedback:[...], count }
 *   GET    /<id>                                          → review
 *   POST   /<id>/hide     · POST /<id>/restore            → { success, ... }
 *   DELETE /<id>                                          → { success, feedback_id }
 * Hide/restore/delete carry no JSON body (no proxy allowlist entry needed).
 */

const BASE = '/api/admin/feedback';

export interface AdminFeedback {
  [key: string]: any;
  feedback_id?: number;
  eid?: number;
  rating?: number;
  content?: string | null;
  hidden?: number | boolean | null;
  date_added?: string | null;
}

const buildQs = (params: Record<string, unknown>) => {
  const clean = Object.entries(params).reduce<Record<string, string>>((acc, [k, v]) => {
    if (v !== undefined && v !== null && v !== '') acc[k] = String(v);
    return acc;
  }, {});
  const qs = new URLSearchParams(clean).toString();
  return qs ? `?${qs}` : '';
};

export const feedbackKeys = {
  all: ['admin-feedback'] as const,
  list: (params: object) => [...feedbackKeys.all, 'list', params] as const,
  detail: (id: number) => [...feedbackKeys.all, 'detail', id] as const,
};

// ─── Queries ─────────────────────────────────────────────────────────────────

/** List reviews by entity (`entity_id`) or free-text `search`; one is required. */
export const useFeedbackListQuery = (
  params: { entity_id?: number; search?: string; page?: number; page_size?: number },
  enabled = true
) => {
  const request = useClientStore((s) => s.request);
  const hasQuery = !!params.entity_id || !!(params.search && params.search.trim());
  return useQuery({
    queryKey: feedbackKeys.list(params),
    enabled: enabled && hasQuery,
    queryFn: withErrorHandling(
      () =>
        request<{ feedback: AdminFeedback[]; count: number }>(
          `${BASE}${buildQs(params)}`
        ),
      'Failed to load reviews'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
    placeholderData: keepPreviousData,
  });
};

export const useFeedbackDetailQuery = (id: number | null, enabled = true) => {
  const request = useClientStore((s) => s.request);
  return useQuery({
    queryKey: feedbackKeys.detail(id ?? -1),
    enabled: enabled && !!id,
    queryFn: withErrorHandling(
      () => request<AdminFeedback>(`${BASE}/${id}`),
      'Failed to load the review'
    ),
    staleTime: DEFAULT_STALE_TIME,
    gcTime: DEFAULT_GC_TIME,
  });
};

// ─── Mutations ───────────────────────────────────────────────────────────────

const useInvalidate = () => {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: feedbackKeys.all });
};

export const useHideFeedbackMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (id: number) => request<any>(`${BASE}/${id}/hide`, 'POST'),
      'Failed to hide the review'
    ),
    onSuccess: invalidate,
  });
};

export const useRestoreFeedbackMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (id: number) => request<any>(`${BASE}/${id}/restore`, 'POST'),
      'Failed to restore the review'
    ),
    onSuccess: invalidate,
  });
};

export const useDeleteFeedbackMutation = () => {
  const request = useClientStore((s) => s.request);
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: withErrorHandling(
      (id: number) => request<any>(`${BASE}/${id}`, 'DELETE'),
      'Failed to delete the review'
    ),
    onSuccess: invalidate,
  });
};
